import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { generalSearchAsync } from '../api/searchApi';
import { deleteResource, editResource } from './resourcesSlice';
import { logout } from "./authSlice";

/* 
searchQuery: {
    text,
    category, 
    tags,
    isZendesk,
    isTrello,
    zendeskQuery: {
        sdkTag,
        gridProviderTag,
        TopicTag,
    },
    trelloQuery: {
        boards: String
    }
}
*/

const initialState = {
    articleResults: [],
    zendeskResults: [],
    trelloResults: [],
    jiraResults: [],
    slackResults: [],
    resourceResults: [],
    docsResults: [],
    showResults: false,
    isZendesk: true,
    isTrello: true,
    isJira: true,
    isSlack: true,
    isDocs: true,
    searchQuery: {text: ""},
    zendeskQuery: {},
    trelloQuery: {},
    slackQuery: {},
    didFetchOptions: false,
}

export const generalSearch = createAsyncThunk(
    'search/generalSearch',
    async (data, thunkAPI) => {
        try {
            const { searchQuery, isZendesk, isJira, isTrello, isSlack, isDocs, zendeskQuery, trelloQuery, slackQuery } = thunkAPI.getState()['search'];
            const body = {
                searchQuery,
                isZendesk,
                isJira,
                isTrello,
                isSlack,
                isDocs,
                zendeskQuery,
                trelloQuery,
                slackQuery,
                slackConfigured: data.slackConfigured,
            };
            const response = await generalSearchAsync(body);
            return response.data;
        } catch(e) {
            return thunkAPI.rejectWithValue(e);
        }
    }
)


const searchSlice = createSlice({
    name: 'search',
    initialState,
    reducers: {
        updateSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
        },
        clearSearchQuery: (state) => {
            state.searchQuery = {text: ""};
            state.isTrello = false;
            state.isZendesk = false;
            state.isJira = false;
            state.isSlack = false;
            state.isDocs = false;
            state.zendeskQuery = {};
            state.trelloQuery = {};
        },
        updateZendeskQuery: (state, action) => {
            state.zendeskQuery = action.payload;
        },
        updateJiraQuery: (state, action) => {
            state.jiraQuery = action.payload;
        },
        updateTrelloQuery: (state, action) => {
            state.trelloQuery = action.payload;
        },
        updateSlackQuery: (state, action) => {
            state.slackQuery = action.payload;
        },
        toggleShowResults: (state, action) => {
            state.showResults = action.payload;
        },
        toggleZendesk: (state, action) => {
            let curr = state.isZendesk;
            state.isZendesk = !curr;
        },
        toggleJira: (state, action) => {
            let curr = state.isJira;
            state.isJira = !curr;
        },
        toggleTrello: (state, action) => {
            let curr = state.isTrello;
            state.isTrello = !curr;
        },
        toggleSlack: (state, action) => {
            let curr = state.isSlack;
            state.isSlack = !curr;
        },
        toggleDocs: (state, action) => {
            let curr = state.isDocs;
            state.isDocs = !curr;
        },
        removeArticleFromResults: (state, action) => {
            // will this be inefficient at scale and when pagination is involved? 
            let id = action.payload;
            let currArticles = [...state.articleResults];
            let index = currArticles.indexOf((article) => article._id == id);
            if (index > -1) {
                currArticles.splice(index, 1);
                state.articleResults = currArticles;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(generalSearch.fulfilled, (state, action) => {
                state.articleResults = action.payload.articles;
                state.resourceResults = action.payload.resources;
                if (state.isZendesk) {
                    state.zendeskResults = action.payload.tickets;
                } else {
                    state.zendeskResults = [];
                }
                if (state.isJira) {
                    state.jiraResults = action.payload.jiras;
                } else {
                    state.jiraResults = [];
                }
                if (state.isTrello) {
                    state.trelloResults = action.payload.trellos;
                } else {
                    state.trelloResults = [];
                }
                if (state.isSlack) {
                    state.slackResults = action.payload.slacks;
                } else {
                    state.slackResults = []; 
                }

                if (state.isDocs) {
                    state.docsResults = action.payload.docs
                } else {
                    state.docsResults = [];
                }
            })
            .addCase(editResource.fulfilled, (state, action) => {
                let data = action.payload.data;
                let resourceIndex = state.resourceResults.findIndex((resource) => resource._id == data._id);
                if (resourceIndex > -1) {
                    let temp = [...state.resourceResults];
                    temp[resourceIndex] = data;
                    state.resourceResults = temp;
                }
            })
            .addCase(deleteResource.fulfilled, (state, action) => {
                let id = action.payload.id;
                let resourceIndex = state.resourceResults.findIndex((resource) => resource._id == id);
                if (resourceIndex > -1) {
                    let temp = [...state.resourceResults];
                    temp.splice(resourceIndex, 1);
                    state.resourceResults = temp; 
                }
            })
            .addCase(logout.fulfilled, (state, action) => {
                state.articleResults = [];
                state.zendeskResults = [];
                state.trelloResults = [];
                state.slackResults = [];
                state.jiraResults = [];
                state.resourceResults = [];
                state.docsResults = [];
                state.showResults = false;
                state.searchQuery = {text: ""};
                state.zendeskQuery = {};
                state.trelloQuery = {};
                state.didFetchOptions = false;
            })
    },
})

export const {
    updateSearchQuery, 
    clearSearchQuery, 
    toggleShowResults, 
    toggleZendesk, 
    toggleJira,
    toggleTrello, 
    toggleSlack,
    toggleDocs,
    updateZendeskQuery, 
    updateTrelloQuery, 
    updateSlackQuery,
    removeArticleFromResults 
} = searchSlice.actions;
export default searchSlice.reducer;